import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { projectsAPI, visitsAPI } from '../services/api';
import './Dashboard.css';

function Dashboard() {
  const [stats, setStats] = useState({
    totalProjects: 0,
    totalVisits: 0,
    activeProjects: 0,
    recentVisits: 0,
  });
  const [recentProjects, setRecentProjects] = useState([]);
  const [recentVisits, setRecentVisits] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);

      // Fetch all projects
      const projectsResponse = await projectsAPI.getAll();
      const projects = projectsResponse.data.data || [];

      // Fetch visits for statistics
      let allVisits = [];
      for (const project of projects.slice(0, 10)) {
        try {
          const visitsResponse = await visitsAPI.getByProject(project.project_id);
          if (visitsResponse.data.data) {
            allVisits = [...allVisits, ...visitsResponse.data.data.map(v => ({
              ...v,
              project_name: project.project_name,
              project_code: project.project_code
            }))];
          }
        } catch (err) {
          console.error(`Error fetching visits for project ${project.project_id}:`, err);
        }
      }

      // Calculate statistics
      const now = new Date();
      const thirtyDaysAgo = new Date(now.setDate(now.getDate() - 30));
      
      const recentVisitsCount = allVisits.filter(v => 
        new Date(v.visit_date) >= thirtyDaysAgo
      ).length;

      setStats({
        totalProjects: projects.length,
        totalVisits: allVisits.length,
        activeProjects: projects.slice(0, 5).length,
        recentVisits: recentVisitsCount,
      });

      // Set recent projects (last 5)
      setRecentProjects(projects.slice(0, 5));

      // Set recent visits (last 5)
      const sortedVisits = allVisits.sort((a, b) => 
        new Date(b.visit_date) - new Date(a.visit_date)
      );
      setRecentVisits(sortedVisits.slice(0, 5));

    } catch (err) {
      console.error('Error fetching dashboard data:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="loading">Loading dashboard...</div>;
  }

  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <h1>GRIHA Site Visit Dashboard</h1>
        <p className="dashboard-subtitle">Overview of all projects and site visits</p>
      </div>

      {/* Statistics Cards */}
      <div className="stats-grid">
        <div className="stat-card primary">
          <div className="stat-icon">📊</div>
          <div className="stat-content">
            <div className="stat-value">{stats.totalProjects}</div>
            <div className="stat-label">Total Projects</div>
          </div>
        </div>

        <div className="stat-card success">
          <div className="stat-icon">🏗️</div>
          <div className="stat-content">
            <div className="stat-value">{stats.totalVisits}</div>
            <div className="stat-label">Total Site Visits</div>
          </div>
        </div>

        <div className="stat-card warning">
          <div className="stat-icon">📅</div>
          <div className="stat-content">
            <div className="stat-value">{stats.recentVisits}</div>
            <div className="stat-label">Visits (Last 30 Days)</div>
          </div>
        </div>

        <div className="stat-card info">
          <div className="stat-icon">✅</div>
          <div className="stat-content">
            <div className="stat-value">{stats.activeProjects}</div>
            <div className="stat-label">Active Projects</div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="quick-actions">
        <h2>Quick Actions</h2>
        <div className="action-buttons">
          <Link to="/projects/new" className="action-btn primary">
            <span className="action-icon">➕</span>
            <span className="action-text">New Project</span>
          </Link>
          <Link to="/projects" className="action-btn secondary">
            <span className="action-icon">📋</span>
            <span className="action-text">View All Projects</span>
          </Link>
        </div>
      </div>

      {/* Recent Projects and Visits */}
      <div className="dashboard-grid">
        {/* Recent Projects */}
        <div className="dashboard-section">
          <div className="section-header">
            <h2>Recent Projects</h2>
            <Link to="/projects" className="view-all-link">View All →</Link>
          </div>
          
          {recentProjects.length === 0 ? (
            <div className="empty-state">
              <p>No projects yet</p>
              <Link to="/projects/new" className="btn btn-primary">
                Create First Project
              </Link>
            </div>
          ) : (
            <div className="projects-list">
              {recentProjects.map((project) => (
                <div key={project.project_id} className="project-item">
                  <div className="project-item-header">
                    <h3>{project.project_name}</h3>
                    <span className="project-badge">{project.project_code}</span>
                  </div>
                  <p className="project-location">📍 {project.location}</p>
                  <div className="project-item-footer">
                    <span className="project-date">
                      Created: {new Date(project.created_at).toLocaleDateString()}
                    </span>
                    <Link 
                      to={`/projects/${project.project_id}/visits/new`}
                      className="btn-small"
                    >
                      New Visit
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Recent Site Visits */}
        <div className="dashboard-section">
          <div className="section-header">
            <h2>Recent Site Visits</h2>
          </div>
          
          {recentVisits.length === 0 ? (
            <div className="empty-state">
              <p>No site visits yet</p>
            </div>
          ) : (
            <div className="visits-list">
              {recentVisits.map((visit) => (
                <div key={visit.visit_id} className="visit-item">
                  <div className="visit-item-header">
                    <h4>{visit.project_name}</h4>
                    <span className="visit-date">
                      {new Date(visit.visit_date).toLocaleDateString()}
                    </span>
                  </div>
                  <p className="visit-code">{visit.project_code}</p>
                  {visit.project_stage && (
                    <p className="visit-stage">Stage: {visit.project_stage}</p>
                  )}
                  <div className="visit-item-footer">
                    <Link 
                      to={`/visits/${visit.visit_id}`}
                      className="btn-small"
                    >
                      View Details
                    </Link>
                    <Link 
                      to={`/visits/${visit.visit_id}/assess`}
                      className="btn-small primary"
                    >
                      Continue Assessment
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* System Info */}
      <div className="system-info">
        <div className="info-item">
          <span className="info-label">GRIHA Version:</span>
          <span className="info-value">V.2015</span>
        </div>
        <div className="info-item">
          <span className="info-label">Assessment Type:</span>
          <span className="info-value">Due Diligence I</span>
        </div>
        <div className="info-item">
          <span className="info-label">Last Updated:</span>
          <span className="info-value">{new Date().toLocaleDateString()}</span>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;
